import { useState, useEffect } from 'react';
import './aqua.css';
import { aquaApi, checkSession, goToLoginPage, aquaLogout } from './aquaApi';
import AquaDashboard    from './pages/AquaDashboard';
import AquaCrops        from './pages/AquaCrops';
import AquaExpenses     from './pages/AquaExpenses';
import AquaFeed         from './pages/AquaFeed';
import AquaChem         from './pages/AquaChem';
import AquaSampling     from './pages/AquaSampling';
import AquaEquipment    from './pages/AquaEquipment';
import AquaSuppliers    from './pages/AquaSuppliers';
import AquaDocuments    from './pages/AquaDocuments';
import AquaHarvest     from './pages/AquaHarvest';
import AquaUsers       from './pages/AquaUsers';
import AquaPurchases   from './pages/AquaPurchases';
import WeatherWidget   from './WeatherWidget';
import MarketWidget   from './MarketWidget';

const NAV = [
  { key: 'dashboard',    icon: '🏠', label: 'Dashboard' },
  { key: 'crops',        icon: '🦐', label: 'Crops' },
  { key: 'expenses',     icon: '💰', label: 'Expenses' },
  { key: 'purchases',    icon: '🛒', label: 'Purchases' },
  { key: 'feed',         icon: '🐟', label: 'Feed Log' },
  { key: 'chem',         icon: '🧪', label: 'Chemicals' },
  { key: 'sampling',     icon: '🔬', label: 'Sampling' },
  { key: 'equipment',    icon: '🔧', label: 'Equipment' },
  { key: 'suppliers',    icon: '🏪', label: 'Suppliers' },
  { key: 'harvest',      icon: '🎣', label: 'Harvest' },
  { key: 'documents',    icon: '📄', label: 'Documents' },
  { key: 'users',        icon: '👥', label: 'Team', adminOnly: true },
];

const TITLES = {
  dashboard:  '🏠 Dashboard',
  crops:      '🦐 Crop Management',
  expenses:   '💰 Expense Ledger',
  purchases:  '🛒 Purchases',
  feed:       '🐟 Feed Log',
  chem:       '🧪 Chemical Log',
  sampling:   '🔬 Sampling & Growth',
  equipment:  '🔧 Equipment',
  suppliers:  '🏪 Suppliers',
  harvest:    '🎣 Harvest',
  documents:  '📄 Documents',
  users:      '👥 Team',
};

function initials(n) {
  if (!n) return '?';
  return n.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

export default function AquaApp({ user, onExit }) {
  const [active, setActive]       = useState('dashboard');
  const [crop, setCrop]           = useState(null);
  const [activeCrops, setActiveCrops] = useState([]);
  const [sidebarOpen, setSidebar] = useState(true);
  const [isMobile, setIsMobile]   = useState(window.innerWidth < 900);
  const [drawerOpen, setDrawer]   = useState(false);
  const [hovered, setHovered]     = useState(false);
  const [theme, setTheme]         = useState(localStorage.getItem('aquaTheme') || 'light');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [role, setRole]           = useState('admin');

  // Listen for session-expiry events fired by aquaApi
  useEffect(() => {
    const handler = () => setSessionExpired(true);
    window.addEventListener('aqua-session-expired', handler);
    return () => window.removeEventListener('aqua-session-expired', handler);
  }, []);

  // Ping session every 10 minutes — warn at ≤ 5 min remaining, redirect when expired
  useEffect(() => {
    const ping = async () => {
      try {
        const s = await checkSession();
        if (!s || !s.authenticated) { setSessionExpired(true); return; }
        if (s.remainingSeconds <= 300) setSessionWarning(true);
        else setSessionWarning(false);
        if (s.role) setRole(s.role);
      } catch (_) {}
    };
    ping(); // initial check
    const id = setInterval(ping, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // Load all active crops — shared by all child pages; auto-select the latest on first load
  const loadActiveCrops = () =>
    aquaApi.getCrops().then(data => {
      if (!data) return;
      setActiveCrops(data.filter(c => c.status === 'ACTIVE'));
      setCrop(prev => {
        if (prev) return prev;
        const sorted = [...data].sort((a, b) => new Date(b.start_date) - new Date(a.start_date));
        return sorted.length > 0 ? sorted[0] : null;
      });
    });

  useEffect(() => { loadActiveCrops(); }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    localStorage.setItem('aquaTheme', theme);
    document.body.setAttribute('data-theme', theme);
  }, [theme]);

  const navigate = (key) => {
    setActive(key);
    setHovered(false);
    if (isMobile) setDrawer(false);
    else setSidebar(false);
  };

  const handleCropSelect = (c) => { setCrop(c); navigate('dashboard'); };

  const displayName = user?.username || user?.name || 'User';

  const renderPage = () => {
    const seedCost = crop ? parseFloat(crop.seed_count || 0) * parseFloat(crop.seed_rate || 0) : 0;
    const props = { cropId: crop?.id, cropCode: crop?.crop_code, seedCost, activeCrops, role, businessTitle: user?.businesstitle || '' };
    switch (active) {
      case 'dashboard': return <AquaDashboard crop={crop} onNavigate={navigate} />;
      case 'crops':     return <AquaCrops onSelect={handleCropSelect} onCropChange={loadActiveCrops} role={role} />;
      case 'expenses':  return <AquaExpenses {...props} />;
      case 'purchases': return <AquaPurchases role={role} activeCrops={activeCrops} />;
      case 'feed':      return <AquaFeed {...props} />;
      case 'chem':      return <AquaChem {...props} />;
      case 'sampling':  return <AquaSampling {...props} />;
      case 'equipment': return <AquaEquipment activeCrops={activeCrops} role={role} />;
      case 'suppliers': return <AquaSuppliers role={role} />;
      case 'harvest':   return <AquaHarvest {...props} />;
      case 'documents': return <AquaDocuments activeCrops={activeCrops} role={role} theme={theme} />;
      case 'users':     return role === 'admin' ? <AquaUsers /> : <div style={{ padding: 40, textAlign: 'center', color: '#c5221f' }}>Access denied</div>;
      default:          return null;
    }
  };

  const isExpanded = !isMobile && (sidebarOpen || hovered);
  const sidebarStyle = {
    width: isExpanded ? 220 : isMobile ? 220 : 60,
    minWidth: isExpanded ? 220 : isMobile ? 220 : 60,
    background: 'linear-gradient(180deg,#0d2137 0%,#0a1e30 100%)',
    height: '100vh', display: 'flex', flexDirection: 'column',
    transition: 'width .2s, min-width .2s',
    position: isMobile ? 'fixed' : 'relative',
    zIndex: isMobile ? 1200 : 1,
    left: isMobile && !drawerOpen ? -240 : 0,
    overflowY: 'auto', overflowX: 'hidden',
    boxShadow: (isMobile && drawerOpen) || isExpanded ? '4px 0 20px rgba(0,0,0,.35)' : 'none',
  };

  const showLabels = isExpanded || (isMobile && drawerOpen);

  if (sessionExpired) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg,#0d2137 0%,#0a1e30 100%)', fontFamily: 'Inter,system-ui,sans-serif' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '40px 48px', textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,.4)', maxWidth: 400 }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#202124', marginBottom: 8 }}>Session Expired</div>
          <div style={{ fontSize: 14, color: '#5f6368', marginBottom: 24 }}>
            Your session has ended. Please log in again to continue.
          </div>
          <button onClick={goToLoginPage}
            style={{ background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 28px', fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
            Log In Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', fontFamily: 'Inter,system-ui,sans-serif', background: theme === 'dark' ? '#0f1623' : '#f4f6f9' }}>
      {/* Mobile overlay */}
      {isMobile && drawerOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1100 }}
          onClick={() => setDrawer(false)} />
      )}

      {/* Sidebar */}
      <aside style={sidebarStyle}
        onMouseEnter={() => { if (!isMobile) setHovered(true); }}
        onMouseLeave={() => { if (!isMobile) setHovered(false); }}
        onTouchStart={() => { if (isMobile && !drawerOpen) setDrawer(true); }}
      >
        {/* Logo */}
        <div style={{ padding: '0 14px', height: 64, display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid rgba(255,255,255,.08)', background: '#001540', flexShrink: 0 }}>
          <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg,#4CCEE8,#0066CC)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 20 }}>
            🦐
          </div>
          {showLabels && (
            <div style={{ overflow: 'hidden' }}>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap' }}>SmaBizZ</div>
              <div style={{ color: '#8899aa', fontSize: 11, marginTop: 1 }}>Aquaculture Mgmt</div>
            </div>
          )}
        </div>

        {/* Active crop pill */}
        {crop && showLabels && (
          <div style={{ margin: '10px 12px', background: 'rgba(26,115,232,.2)', border: '1px solid rgba(26,115,232,.4)', borderRadius: 8, padding: '6px 10px' }}>
            <div style={{ color: '#90caf9', fontSize: 11, fontWeight: 600 }}>ACTIVE CROP</div>
            <div style={{ color: '#e2e8f0', fontSize: 13, fontWeight: 700 }}>{crop.crop_code}</div>
            <div style={{ color: '#718096', fontSize: 11 }}>{crop.total_acres} Acres · {crop.num_ponds} Ponds</div>
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, padding: '8px 0' }}>
          {NAV.filter(item => !item.adminOnly || role === 'admin').map(item => (
            <div key={item.key}
              onClick={() => navigate(item.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: showLabels ? '10px 16px' : '10px 0',
                justifyContent: showLabels ? 'flex-start' : 'center',
                cursor: 'pointer', borderRadius: 8, margin: '1px 8px',
                background: active === item.key ? 'rgba(26,115,232,.25)' : 'transparent',
                borderLeft: active === item.key ? '3px solid #1a73e8' : '3px solid transparent',
                transition: 'background .15s',
              }}
            >
              <span style={{ fontSize: 18, flexShrink: 0 }}>{item.icon}</span>
              {showLabels && (
                <span style={{ color: active === item.key ? '#e2e8f0' : '#a0aec0', fontSize: 14, fontWeight: active === item.key ? 700 : 500 }}>
                  {item.label}
                </span>
              )}
            </div>
          ))}
        </nav>

        {/* Sidebar footer */}
        <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg,#1a73e8,#34a853)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>
            {initials(displayName)}
          </div>
          {showLabels && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
              <div style={{ fontSize: 11, color: role === 'admin' ? '#34a853' : role === 'adduser' ? '#fbbc04' : '#9aa0a6' }}>
                {role === 'admin' ? '⭐ Admin' : role === 'adduser' ? '✏️ Add User' : '👁 Read Only'}
              </div>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {/* Business title banner */}
        {user?.businesstitle && (
          <div style={{ width: '100%', background: 'linear-gradient(90deg,#001540,#002366)', textAlign: 'center', padding: '12px 24px', flexShrink: 0, boxShadow: '0 2px 8px rgba(0,0,0,.3)', zIndex: 20 }}>
            <span style={{ fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '.6px', textShadow: '0 1px 3px rgba(0,0,0,.2)' }}>{user.businesstitle}</span>
          </div>
        )}
        {/* Topbar */}
        <div style={{ height: 56, display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px',
          background: theme === 'dark' ? '#1a2535' : '#fff',
          borderBottom: theme === 'dark' ? '1px solid rgba(255,255,255,.07)' : '1px solid #e8eaed',
          boxShadow: '0 1px 4px rgba(0,0,0,.08)', flexShrink: 0, zIndex: 10 }}>
          <button
            onClick={() => isMobile ? setDrawer(!drawerOpen) : setSidebar(!sidebarOpen)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#5f6368', padding: 4, flexShrink: 0 }}>
            ☰
          </button>
          <div style={{ fontWeight: 700, fontSize: 16, color: theme === 'dark' ? '#e2e8f0' : '#202124', flex: 1 }}>
            {TITLES[active]}
          </div>
          {crop && (
            <div style={{ background: '#e8f0fe', color: '#1a73e8', borderRadius: 16, padding: '3px 12px', fontSize: 13, fontWeight: 600 }}>
              {crop.crop_code}
            </div>
          )}
          {!isMobile && <MarketWidget theme={theme} />}
          <WeatherWidget theme={theme} compact={isMobile} />
          <button onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, padding: 4 }}>
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
          {onExit && (
            <button onClick={onExit}
              style={{ background: '#fce8e6', color: '#c5221f', border: 'none', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              ← Back
            </button>
          )}
          <button onClick={aquaLogout}
            title="Sign out"
            style={{ background: 'none', border: '1px solid #e8eaed', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#5f6368', display: 'flex', alignItems: 'center', gap: 5 }}>
            🚪 Sign Out
          </button>
        </div>

        {/* Session expiry warning */}
        {sessionWarning && (
          <div style={{ padding: '8px 16px', background: '#fef3c7', borderBottom: '1px solid #fbbf24',
            display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, flexShrink: 0 }}>
            <span>⚠️ Your session expires soon.</span>
            <button onClick={goToLoginPage}
              style={{ background: '#f59e0b', color: '#fff', border: 'none', borderRadius: 6,
                padding: '3px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              Refresh Session
            </button>
            <button onClick={() => setSessionWarning(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#92400e', fontWeight: 700, marginLeft: 'auto' }}>
              ×
            </button>
          </div>
        )}

        {/* Page content */}
        <div style={{ flex: 1, overflow: 'auto', padding: 16,
          ...(theme !== 'dark' && {
            backgroundImage: `url(${process.env.PUBLIC_URL}/aqua-bg.svg)`,
            backgroundSize: '360px 260px',
            backgroundRepeat: 'repeat',
          })
        }}>
          {renderPage()}
        </div>

        {/* Footer */}
        <div style={{ height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: theme === 'dark' ? '#0f1623' : '#f4f6f9',
          borderTop: theme === 'dark' ? '1px solid rgba(255,255,255,.06)' : '1px solid #e8eaed',
          fontSize: 11, color: '#9aa0a6', flexShrink: 0 }}>
          CHMR Aquaculture Management · {new Date().getFullYear()} · SmaBizZ
        </div>
      </div>
    </div>
  );
}
